import { get as getDeviceId, peek as peekDeviceId } from './fingerprintManager.js';
import { getLocalISODateString } from './dateUtils.js';
import { getCompositeUserId } from './userIdUtils.js';
import { checkStatus } from './statusChecker.js';

const SHOULD_SEND_ANALYTICS = false; // Backend endpoint not ready, using Amplitude only

// Helper to check if we're in a test environment
const isTestEnvironment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const hasTestApiKey = /test-api-key/.test(document.body.innerHTML);
  return hostname === 'localhost' || hostname === '127.0.0.1' || hasTestApiKey;
};

export const ANALYTICS_EVENTS = {
  // Chat widget
  CHAT_WIDGET_INITIALIZED: 'Chat Widget Initialized',
  CHAT_WIDGET_VISIBLE: 'Chat Widget Visible',
  PROMPT_SUBMITTED: 'Prompt Submitted',
  PROMPT_SUBMITTED_VIA_URL: 'Prompt Submitted Via URL',
  PROMPT_SUBMITTED_VIA_ATTRIBUTE: 'Prompt Submitted Via Attribute',
  FOLLOW_UP_PROMPT_SUBMITTED: 'Follow-up Prompt Submitted',
  CITATION_CLICKED: 'Citation Clicked',
  CITATION_CAROUSEL_ITEM_CLICKED: 'Citation Carousel Item Clicked',
  AD_LINK_CLICKED: 'Ad Link Clicked', // For native ads (currently not in use)
  AD_IFRAME_LOADED: 'Ad Iframe Loaded', // For iframe ads (current implementation)
  RELATED_QUESTION_CLICKED: 'Related Question Clicked',
  NEW_SEARCH_CLICKED: 'New Search Clicked',
  RESPONSE_LIKE_CLICKED: 'Response Like Clicked',
  RESPONSE_DISLIKE_CLICKED: 'Response Dislike Clicked',
  RESPONSE_FEEDBACK_SUBMITTED: 'Response Feedback Submitted',
  SHARE_BUTTON_CLICKED: 'Share Button Clicked',
  // Search widget
  SEARCH_WIDGET_INITIALIZED: 'Search Widget Initialized',
  SEARCH_WIDGET_VISIBLE: 'Search Widget Visible',
  SEARCH_PROMPT_SUBMITTED: 'Search Prompt Submitted',
  // Summary widget
  SUMMARY_WIDGET_INITIALIZED: 'Summary Widget Initialized',
  SUMMARY_REQUESTED: 'Summary Requested', // For when the summary process starts
  SUMMARY_GENERATED: 'Summary Generated', // For when the summary is complete

  // Currently these are not used in lit / web components
  PROMPT_DISPLAYED: 'Prompt Displayed',
  RESPONSE_DISPLAYED: 'Response Displayed',
  RESPONSE_CITATIONS_DISPLAYED: 'Response Citations Displayed',
  RELATED_QUESTIONS_DISPLAYED: 'Related Questions Displayed',
  ATTRIBUTION_BAR_DISPLAYED: 'Attribution Bar Displayed',
  CITATION_CAROUSEL_DISPLAYED: 'Citation Carousel Displayed',
  FOLLOW_UP_PROMPT_DISPLAYED: 'Follow-up Prompt Displayed',
  NEW_SEARCH_BUTTON_DISPLAYED: 'New Search Button Displayed',
  SEARCH_PROMPT_DISPLAYED: 'Search Prompt Displayed',
};

class AnalyticsService {
  constructor() {
    this._initialized = false;
    this._analyticsConfig = {};
  }

  _getEnvironmentFromUrl(url) {
    if (!url) return 'unknown';
    try {
      const hostname = new URL(url).hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'local';
      }

      // Handle prorata.ai (new)
      if (hostname === 'api.prorata.ai') {
        return 'production';
      }
      const prorataMatch = hostname.match(/^api\.(\w+)\.prorata\.ai$/);
      if (prorataMatch && prorataMatch[1]) {
        return prorataMatch[1]; // e.g., "development", "staging"
      }

      // Handle gist.ai (legacy)
      if (hostname === 'api.gist.ai') {
        return 'production';
      }
      const gistMatch = hostname.match(/^api\.(\w+)\.gist\.ai$/);
      if (gistMatch && gistMatch[1]) {
        return gistMatch[1]; // e.g., "sandbox", "future... gist.ai"
      }

      return 'unknown';
    } catch (error) {
      console.error('Error parsing environment from URL:', error);
      return 'unknown';
    }
  }

  _init(config = {}) {
    if (this._initialized) {
      // This is now handled in the widget, but we keep the warning as a safeguard
      if (typeof window !== 'undefined') {
        console.warn('Analytics already initialized. Subsequent init calls are currently ignored.');
      }
      return;
    }
    // Use build-time injected Amplitude API key if not provided in config
    const buildTimeAmplitudeKey = typeof __AMPLITUDE_API_KEY__ !== 'undefined' ? __AMPLITUDE_API_KEY__ : '';

    this._analyticsConfig = {
      apiKey: config.apiKey,
      userId: config.userId,
      widgetType: config.widgetType || 'unknown',
      apiBaseUrl: config.apiBaseUrl,
      environment: this._getEnvironmentFromUrl(config.apiBaseUrl),
      widgetVersion: typeof __WIDGET_VERSION__ !== 'undefined' ? __WIDGET_VERSION__ : 'unknown',
      amplitudeApiKey: config.amplitudeApiKey || buildTimeAmplitudeKey || null,
      amplitudeModule: null // Will be set via setAmplitudeModule for bundling control
    };
    this._initialized = true;

    if (typeof window !== 'undefined') {
      const safeConfig = {
        ...this._analyticsConfig,
        apiKey: this._analyticsConfig.apiKey ? `${this._analyticsConfig.apiKey.substring(0, 12)}...` : undefined,
        amplitudeApiKey: this._analyticsConfig.amplitudeApiKey ? `${this._analyticsConfig.amplitudeApiKey.substring(0, 8)}...` : undefined,
      };
      const deviceId = peekDeviceId();

    }
  }

  /**
   * Set the Amplitude module for tracking.
   * This is used to inject the Amplitude module from the widget that needs it,
   * preventing the SDK from being bundled into widgets that don't use it.
   * @param {Object} amplitudeModule - The amplitudeAnalytics module
   */
  _setAmplitudeModule(amplitudeModule) {
    this._analyticsConfig.amplitudeModule = amplitudeModule;

    // Initialize Amplitude if API key is available
    if (amplitudeModule && this._analyticsConfig.amplitudeApiKey) {
      amplitudeModule.initAmplitude(this._analyticsConfig.amplitudeApiKey, {
        userId: this._analyticsConfig.userId,
        widgetType: this._analyticsConfig.widgetType
      });
    }
  }

  async _track(eventName, eventProperties = {}) {
    if (!this._initialized) {
      if (typeof window !== 'undefined') {
        console.warn('Analytics.track called before init. Event not tracked:', eventName);
      }
      return;
    }

    // Get device_id first to ensure both composite userId and device_id use the same value
    const deviceId = await getDeviceId();
    if (!deviceId) {
      if (typeof window !== 'undefined') {
        console.warn('Analytics.track skipped because device_id is unavailable');
      }
      return;
    }
    
    const compositeUserId = await getCompositeUserId(this._analyticsConfig.userId);
    const tempPayload = {
      eventName: eventName,
      properties: {
        widgetType: this._analyticsConfig.widgetType,
        environment: this._analyticsConfig.environment,
        widgetVersion: this._analyticsConfig.widgetVersion,
        timestamp: new Date().toISOString(),
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        user_id: compositeUserId,
        device_id: String(deviceId),
        ...eventProperties,
      }
    };

    // Send to Amplitude if module is available (injected by widget that needs it)
    // This prevents bundling Amplitude SDK into widgets that don't use it
    if (this._analyticsConfig.amplitudeModule && this._analyticsConfig.amplitudeApiKey) {
      this._analyticsConfig.amplitudeModule.trackAmplitudeEvent(eventName, eventProperties).catch(err => {
        // Silently ignore Amplitude errors to not block backend tracking
        if (isTestEnvironment()) {
          console.warn('Amplitude tracking failed (non-blocking):', err);
        }
      });
    }

    if (!SHOULD_SEND_ANALYTICS || isTestEnvironment()) {
      return;
    }

    if (!this._analyticsConfig.apiBaseUrl) {
      if (typeof window !== 'undefined') {
        console.warn('Analytics.track called without apiBaseUrl configured. Event not tracked:', eventName);
      }
      return;
    }

    try {
      const response = await fetch(`${this._analyticsConfig.apiBaseUrl}/v1/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this._analyticsConfig.apiKey}`,
          'X-User-Id': compositeUserId,
          'X-Client-Datetime': getLocalISODateString()
        },
        body: JSON.stringify(tempPayload),
        // Use `keepalive` to ensure the request completes even if the page unloads.
        // This is critical for events fired just before a redirect, such as in the searchWidget,
        // to prevent the browser from cancelling the request.
        keepalive: true
      });

      if (!response.ok) {
        console.error('Analytics track request failed:', response.status, await response.text());
        
        // Check status.json after analytics failure (silently, no UI)
        // This is for consistency and debugging purposes
        try {
          const statusCheck = await checkStatus(__STATUS_CDN_BASE_URL__);
          if (statusCheck && (statusCheck.status === 'maintenance' || statusCheck.status === 'outage')) {
            console.debug('Service status detected:', statusCheck.status, statusCheck.message);
          }
        } catch (statusError) {
          // Ignore status check errors for analytics
        }
      }
    } catch (error) {
      console.error('Error sending analytics data:', error);
      
      // Check status.json after network error (silently, no UI)
      // This is for consistency and debugging purposes
      try {
        const statusCheck = await checkStatus(__STATUS_CDN_BASE_URL__);
        if (statusCheck && (statusCheck.status === 'maintenance' || statusCheck.status === 'outage')) {
          console.debug('Service status detected:', statusCheck.status, statusCheck.message);
        }
      } catch (statusError) {
        // Ignore status check errors for analytics
      }
    }
  }
}

const analyticsInstance = new AnalyticsService();

export function init(config) {
  analyticsInstance._init(config);
}

export async function track(eventName, eventProperties) {
  await analyticsInstance._track(eventName, eventProperties);
}

export const Analytics = {
  init,
  track,
  // Expose method to inject Amplitude module for bundling control
  // Widgets that need Amplitude tracking should:
  // 1. Import amplitudeAnalytics.js themselves
  // 2. Call Analytics.setAmplitudeModule(amplitudeModule) after init
  setAmplitudeModule: (module) => analyticsInstance._setAmplitudeModule(module),
};